import { getAvailableSheets, validateSheetId } from '../config/SheetConfig.js';
import { ROUTING_ENABLED } from '../config/AppConfig.js';

export class Router {
    static LOGGING_ENABLED = true;
    constructor(navigationController) {
        if (Router.LOGGING_ENABLED) console.log('Router instantiated.');
        this.navigationController = navigationController;
        this.isHandlingPopstate = false; // Flag to prevent feedback during popstate
        this.routingEnabled = ROUTING_ENABLED;
        // Queue for rapid back/forward sequences
        this.popstateQueue = [];
        this.isProcessingQueue = false;
        // Abort signal for in-flight driveNavigationController
        this._abortCurrentNavigation = false;
        
        if (!this.routingEnabled) {
            console.log('Router: Routing disabled by config');
        }
    }

    async init() {
        if (!this.routingEnabled) {
            console.log('Router: Skipping initialization (routing disabled)');
            return;
        }
        
        if (Router.LOGGING_ENABLED) console.log('Router ready.');
        
        // Wait for NavigationController to be ready
        if (this.navigationController && this.navigationController.readyPromise) {
            console.log('Router: Waiting for NC ready state');
            await this.navigationController.readyPromise;
        }
        
        // Subscribe to state changes
        this.subscribeToNavigationController();
        
        // Set up popstate handler (queue-based)
        window.addEventListener('popstate', () => this.handlePopState());
        
        // Process initial URL only after ContactSheet is fully ready (imageMapping present)
        if (!this.navigationController?.components?.contactSheet?.imageMapping) {
            console.log('Router: Deferring initial URL processing until ContactSheet imageMapping is ready');
            // Subscribe once to state change from NC after ContactSheet initialization
            const checkReadyAndHandle = () => {
                if (this.navigationController?.components?.contactSheet?.imageMapping) {
                    this.handlePopState();
                } else {
                    // Still not ready — wait for NC readyPromise resolution then retry
                    this.navigationController.readyPromise?.then(() => {
                        if (this.navigationController?.components?.contactSheet?.imageMapping) {
                            this.handlePopState();
                        }
                    });
                }
            };
            checkReadyAndHandle();
        } else {
            this.handlePopState();
        }
    }

    subscribeToNavigationController() {
        if (Router.LOGGING_ENABLED) console.log('Router subscribing to NavigationController state/position changes.');
        if (this.navigationController && this.navigationController.registerStateChangeCallback) {
            this.navigationController.registerStateChangeCallback((state, position) => {
                this.onNavigationControllerChange(state, position);
            });
            if (Router.LOGGING_ENABLED) console.log('Router now listening to NavigationController events.');
        }
    }

    async handlePopState() {
        const route = this.parseUrl();
        if (Router.LOGGING_ENABLED) console.log('Router: popstate event, parsed route:', route);
        this.enqueuePopstate(route);
    }

    enqueuePopstate(route) {
        // Deduplicate consecutive identical routes
        const last = this.popstateQueue[this.popstateQueue.length - 1];
        if (last && last.type === route.type && last.imageName === route.imageName && last.sheetId === route.sheetId) {
            return;
        }
        this.popstateQueue.push(route);
        // If already processing, abort current in-flight navigation so we skip to latest
        if (this.isProcessingQueue) {
            this._abortCurrentNavigation = true;
            return;
        }
        this.processPopstateQueue();
    }

    async processPopstateQueue() {
        if (this.isProcessingQueue) return;
        this.isProcessingQueue = true;
        this.isHandlingPopstate = true; // Suppress NC->URL updates while processing queue
        try {
            while (this.popstateQueue.length > 0) {
                // Skip intermediate routes — only animate to the final destination
                const routeToProcess = this.popstateQueue[this.popstateQueue.length - 1];
                this.popstateQueue = [];
                this._abortCurrentNavigation = false;

                // Force-clear NC transition state before driving to new route
                const nc = this.navigationController;
                if (nc) {
                    nc.forceExitRapidNavigationMode();
                    nc.clearNavigationQueue();
                    nc.isTransitioning = false;
                    nc._clearTransitionTimeout();
                }

                try {
                    await this.driveNavigationController(routeToProcess);
                } catch (error) {
                    console.error('Router: Error during queued navigation:', error);
                }
            }
        } finally {
            this.isProcessingQueue = false;
            this.isHandlingPopstate = false;
            this._abortCurrentNavigation = false;
        }
    }

    async driveNavigationController(route) {
        const nc = this.navigationController;
        if (!nc) return;

        // Wait for NavigationController to be ready if it isn't yet
        if (!nc.isReady) {
            if (Router.LOGGING_ENABLED) console.log('Router: Waiting for NavigationController to be ready...');
            if (nc.readyPromise) {
                await nc.readyPromise;
            } else {
                console.warn('Router: NavigationController not ready and no readyPromise available');
                return;
            }
        }

        if (this._abortCurrentNavigation) return;

        if (Router.LOGGING_ENABLED) {
            console.log(`Router: Driving NC from ${nc.state} to ${route.type}${route.imageName ? ' for ' + route.imageName : ''}`);
        }

        // For overview/collection
        if (route.type === 'collection') {
            // Force instant state reset instead of animating through intermediate states
            if (nc.state === 'DETAIL_VIEW') {
                await nc.navigate('close-detail');
                if (this._abortCurrentNavigation) return;
                await nc.navigate('zoom-out');
            } else if (nc.state === 'ZOOMED_IN') {
                await nc.navigate('zoom-out');
            }
            return;
        }

        // Validate image name
        if (!route.imageName) {
            console.log('Router: No image name provided, redirecting to overview');
            this.handleInvalidUrl();
            return;
        }

        console.log(`Router: Validating image name: ${route.imageName}`);

        let position = null;

        // URLs have the clean name without number prefix, so we need to try all possible prefixes
        for (let i = 1; i <= 36; i++) {
            const paddedNum = String(i).padStart(2, '0');
            const filenameWithPrefix = `${paddedNum}-${route.imageName}.jpg`;
            position = nc.getPositionByImageName(filenameWithPrefix);
            if (position) break;
        }

        if (!position) {
            console.log(`Router: Image not found, redirecting to overview`);
            if (Router.LOGGING_ENABLED) console.log(`Router: Invalid image name [${route.imageName}]`);
            this.handleInvalidUrl();
            return;
        }

        // Get current state and position
        const currentPos = nc.getCurrentPosition();
        const isCurrentImage = currentPos && currentPos.row === position.row && currentPos.col === position.col;

        // Handle navigation based on target route type and current state
        if (route.type === 'image') {
            // Target: zoomed view of image
            if (nc.state === 'OVERVIEW') {
                console.log('NC: Restoring state for deep link to ZOOMED_IN');
                nc.updatePosition(position.row, position.col);
                await nc.navigate('zoom-in');
                if (this._abortCurrentNavigation) return;
                nc.syncUIElements?.();
                console.log('NC: State restoration complete');
            } else if (nc.state === 'DETAIL_VIEW') {
                await nc.navigate('close-detail');
                if (this._abortCurrentNavigation) return;
                if (!isCurrentImage) {
                    await nc.navigateToPosition(position.row, position.col);
                }
            } else if (nc.state === 'ZOOMED_IN' && !isCurrentImage) {
                await nc.navigateToPosition(position.row, position.col);
            }
        } else if (route.type === 'detail') {
            // Target: detail view of image
            if (nc.state === 'OVERVIEW') {
                console.log('Router: Multi-step navigation required');
                console.log('NC: Restoring state for deep link to DETAIL_VIEW');
                nc.updatePosition(position.row, position.col);
                console.log('NC: Step 1/3: position update');
                await nc.navigate('open-detail');
                if (this._abortCurrentNavigation) return;
                nc.syncUIElements?.();
                console.log('NC: State restoration complete');
            } else if (nc.state === 'ZOOMED_IN') {
                if (!isCurrentImage) {
                    await nc.navigateToPosition(position.row, position.col);
                    if (this._abortCurrentNavigation) return;
                }
                await nc.navigate('detail-view');
            } else if (nc.state === 'DETAIL_VIEW' && !isCurrentImage) {
                await nc.navigateToPosition(position.row, position.col);
            }
        }
    }

    handleInvalidUrl() {
        if (Router.LOGGING_ENABLED) console.log('Router: Invalid URL, resetting to overview');
        console.log('Router: Invalid state detected, falling back to overview');
        const sheetId = this.getActiveSheetId();
        history.replaceState(null, '', `/${sheetId}`);
        console.log('NC: navigate(\'back-to-contact\') from invalid state');
        if (this.navigationController && this.navigationController.state !== 'OVERVIEW') {
            this.navigationController.navigate('back-to-contact');
        }
    }

    parseUrl() {
        const path = window.location.pathname;
        const parts = path.split('/').filter(Boolean);
        
        // If no sheet provided, default to collection for the current active sheet
        if (parts.length < 1) {
            return { type: 'collection', imageName: null, sheetId: this.getActiveSheetId() };
        }

        const sheetId = parts[0];
        if (parts.length === 1) {
            return { type: 'collection', imageName: null, sheetId };
        }
        
        const imageName = parts[1];
        const type = parts.length === 3 && parts[2] === 'detail' ? 'detail' : 'image';
        
        return { type, imageName, sheetId };
    }

    onNavigationControllerChange(state, position) {
        if (!this.routingEnabled) return;
        
        // Skip URL updates if we're handling a popstate event
        if (this.isHandlingPopstate) {
            if (Router.LOGGING_ENABLED) {
                console.log('Router: Skipping URL update during popstate handling');
            }
            return;
        }
        
        const url = this.formatUrl(state, position);
        
        // Only push if URL actually changed
        if (window.location.pathname !== url) {
            if (Router.LOGGING_ENABLED) {
                console.log(`Router: Pushing new URL: ${url}`);
            }
            history.pushState(null, '', url);
        }
    }

    formatUrl(state, position) {
        const sheetId = this.getActiveSheetId();
        let url = `/${sheetId}`;
        
        if ((state === 'ZOOMED_IN' || state === 'DETAIL_VIEW') && position?.imageName) {
            const nameWithoutExt = position.imageName.replace(/\.[^/.]+$/, '');
            // Remove leading number prefix (e.g., "16-280-spear-st" -> "280-spear-st")
            const cleanName = nameWithoutExt.replace(/^\d+-/, '');
            url += `/${cleanName}`;
            
            if (state === 'DETAIL_VIEW') {
                url += '/detail';
            }
        }
        
        return url;
    }

    // Determine the active sheetId from NC/components; fallback to sosf
    getActiveSheetId() {
        return this.navigationController?.components?.contactSheet?.sheetId || 'sosf';
    }
} 